/**
 * <quantity-input> Web Component
 * A simple increment / decrement control that syncs with the Cart API.
 *
 * Expected inner markup:
 *   <button data-action="decrease">-</button>
 *   <input type="number" data-line="1" value="1" min="0" max="99">
 *   <button data-action="increase">+</button>
 *
 * Behaviour:
 *   - Decrease clicks reduce the value by 1. Reaching 0 triggers a remove
 *     (RA.cart.change with quantity 0).
 *   - Increase clicks add 1, up to max 99.
 *   - Direct input changes are debounced by 300 ms before calling the API.
 *
 * Depends on: RA.cart (theme.js)
 */

if (!customElements.get('quantity-input')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this._debounceTimer = null;
      this._updating = false;
    }

    connectedCallback() {
      this.init();
    }

    /**
     * (Re-)bind event listeners. Called on connectedCallback and can be
     * called manually after the element's innerHTML is replaced (e.g.
     * by the cart drawer re-render).
     */
    init() {
      this.input = this.querySelector('input[type="number"]');
      this.decreaseBtn = this.querySelector('[data-action="decrease"]');
      this.increaseBtn = this.querySelector('[data-action="increase"]');

      if (!this.input) return;

      /* Remove old listeners to prevent duplicates when init() is called
         multiple times (e.g. after cart re-render). */
      this._removeListeners();

      /* Create bound handlers */
      this._onDecrease = this._handleDecrease.bind(this);
      this._onIncrease = this._handleIncrease.bind(this);
      this._onInputChange = this._handleInputChange.bind(this);

      if (this.decreaseBtn) {
        this.decreaseBtn.addEventListener('click', this._onDecrease);
      }
      if (this.increaseBtn) {
        this.increaseBtn.addEventListener('click', this._onIncrease);
      }
      this.input.addEventListener('change', this._onInputChange);
    }

    disconnectedCallback() {
      this._removeListeners();
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
      }
    }

    _removeListeners() {
      if (this.decreaseBtn && this._onDecrease) {
        this.decreaseBtn.removeEventListener('click', this._onDecrease);
      }
      if (this.increaseBtn && this._onIncrease) {
        this.increaseBtn.removeEventListener('click', this._onIncrease);
      }
      if (this.input && this._onInputChange) {
        this.input.removeEventListener('change', this._onInputChange);
      }
    }

    /* ------------------------------------------------------------------
       Button handlers
       ------------------------------------------------------------------ */

    _handleDecrease(e) {
      e.preventDefault();
      if (this._updating) return;

      var currentValue = parseInt(this.input.value, 10) || 0;
      var newValue = Math.max(0, currentValue - 1);
      this.input.value = newValue;

      this._submitChange(newValue);
    }

    _handleIncrease(e) {
      e.preventDefault();
      if (this._updating) return;

      var currentValue = parseInt(this.input.value, 10) || 0;
      var max = parseInt(this.input.max, 10) || 99;
      var newValue = Math.min(max, currentValue + 1);
      this.input.value = newValue;

      this._submitChange(newValue);
    }

    /* ------------------------------------------------------------------
       Direct input change (debounced)
       ------------------------------------------------------------------ */

    _handleInputChange() {
      if (this._updating) return;

      var self = this;
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
      }

      this._debounceTimer = setTimeout(function () {
        var value = parseInt(self.input.value, 10);

        /* Clamp to valid range */
        if (isNaN(value) || value < 0) {
          value = 0;
        }
        var max = parseInt(self.input.max, 10) || 99;
        if (value > max) {
          value = max;
        }

        self.input.value = value;
        self._submitChange(value);
      }, 300);
    }

    /* ------------------------------------------------------------------
       API call
       ------------------------------------------------------------------ */

    async _submitChange(quantity) {
      var line = parseInt(this.input.dataset.line, 10);
      if (!line) return;

      this._updating = true;
      this._setDisabledState(true);

      try {
        await RA.cart.change(line, quantity);
      } catch (err) {
        console.error('Quantity update error:', err);
        /* The cart:updated event from a successful call will refresh the
           drawer and reset values. On error we just re-enable the controls
           so the user can try again. */
      } finally {
        this._updating = false;
        this._setDisabledState(false);
      }
    }

    /* ------------------------------------------------------------------
       UI helpers
       ------------------------------------------------------------------ */

    _setDisabledState(disabled) {
      if (this.decreaseBtn) this.decreaseBtn.disabled = disabled;
      if (this.increaseBtn) this.increaseBtn.disabled = disabled;
      if (this.input) this.input.disabled = disabled;
    }
  }

  customElements.define('quantity-input', QuantityInput);
}
